import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { EditableTextbox } from "@/overlay/components/CommonParts/EditableTextbox";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";

import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { FeatureHubGuard } from "@/featureFlags";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { EnsembleMemberSelect } from "@/overlay/components/Ensamble";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { CalibratedToggle } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { LocationTableActionPanel } from "@/overlay/components/LayerStackWindow/LocationTableActionPanel";
import { useHandleEditLocations } from "@/overlay/components/LayerStackWindow/LocationTableSettings/useLocationManager";
import { useLocationTableHooks } from "@/overlay/components/LayerStackWindow/LocationTableSettings/useLocationTableHooks";
import { OptionSeparatorModel } from "@/overlay/components/LayerStackWindow/PlotSerieOptions";
import { TableExportMenu } from "@/overlay/components/LayerStackWindow/TableExportMenu";
import { Section } from "@/overlay/components/LayerStackWindow/WeatherTableSettings/Section";
import { usePartialWeatherParameter } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/usePartialWeatherParameter";
import { WeatherParameterForm } from "@/overlay/components/WeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import type { LocationTableState } from "@/reducer/LocationTablesState";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import {
  getStandardSearchEngine,
  stationModels_TEMPORAL,
  useWeatherParamModels,
  winterdienstStationModels_TEMPORAL,
} from "@/weather-parameters";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import {
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  parameterToString,
} from "@mm/weather-parameter-utils";
import { MmCheckbox } from "@mm/zeus-react";
import { useFlag } from "@openfeature/react-sdk";
import React, { useEffect, useMemo, useState } from "react";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";
import { normalizeModelParamDependencies } from "../TableHelper";

interface TableSettingProps {
  locationTable: LocationTableState;
  clearTable: () => void;
}

export function LocationTableSettings({ locationTable, clearTable }: TableSettingProps) {
  // TODO move threshold handling to the hook to keep the component clean.
  const {
    exportAllowed,
    handleGroupsChange,
    handleColumnGroupingChange,
    handleRowGroupingChange,
    handleThresholdsChange,
    handleAdvancedThresholdsChange,
    handleTitleChange,
    handleTableChange,
    handleCSVExport,
    handlePDFExport,
  } = useLocationTableHooks(locationTable);
  const openLocationManager = useHandleEditLocations(locationTable.groups || [], handleGroupsChange);
  const parameterUnit = locationTable.parameterUnit;
  const searchEngine = getStandardSearchEngine();
  const { parameter } = usePartialWeatherParameter(parameterUnit || "t_2m:C", "TableParameter", searchEngine);
  const [currentParam, setCurrentParam] = useState<PartialWeatherParameter | null>(null);
  const [selectedModelSchema, setSelectedModelSchema] = useState<ModelSchema | null>(null);
  const weatherModels = useWeatherParamModels();
  const { value: winterdienstStationsEnabled } = useFlag("winterdienst-stations", false);

  useEffect(() => {
    setCurrentParam(parameter ?? null);
  }, [parameter]);

  const models = useMemo<ModelSchema[] | null>(() => {
    if (!weatherModels) {
      return null;
    }

    return [
      ...weatherModels.filter((model) =>
        currentParam ? currentParam.parameter.available_models?.includes(model.identifier) : false,
      ),
      OptionSeparatorModel,
      ...stationModels_TEMPORAL,
      ...(winterdienstStationsEnabled ? winterdienstStationModels_TEMPORAL : []),
    ];
  }, [weatherModels, currentParam, winterdienstStationsEnabled]);

  useEffect(() => {
    if (weatherModels) {
      const modelSchema = weatherModels.find((model) => model.identifier === locationTable.model);
      setSelectedModelSchema(modelSchema ?? null);
    }
  }, [weatherModels, locationTable.model]);

  function updateWeatherParameter(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    if (currentParam) {
      setCurrentParam(param);
      const newParameter = parameterToString(param);
      if (newParameter && newParameter.missingFields.length === 0) {
        updateTableNormalized({ parameterUnit: newParameter.formatted });
      }
    }
  }

  function updateTableNormalized(patch: Partial<LocationTableState>) {
    const normalizedPatch = normalizeLocationTableChange(locationTable, patch, models);
    handleTableChange(normalizedPatch);
  }

  function normalizeLocationTableChange(
    current: LocationTableState,
    patch: Partial<LocationTableState>,
    weatherModels: ModelSchema[] | null,
  ): Partial<LocationTableState> {
    return normalizeModelParamDependencies(current, patch, weatherModels);
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!locationTable.thresholds) {
      console.error("Threshold orders were swapped but no data is available");
      return;
    }

    const copyOfThresholds = [...locationTable.thresholds];

    const fromIdx = dragStart.index;
    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }
    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    handleThresholdsChange(copyOfThresholds);
  }

  const hasPolygon = !!locationTable.groups?.[0]?.locations?.[0]?.polygon;
  const customParameterTitle =
    locationTable.title?.trim() && locationTable.title !== parameterUnit ? locationTable.title : null;

  return (
    <>
      <div className="title-row">
        <EditableTextbox
          className={"editable-map-title"}
          value={locationTable.title ?? ""}
          placeholder={locationTable.title === "" ? t`Untitled Table` : undefined}
          blurOnEnterKey
          onSave={(newTitle) => handleTitleChange(newTitle)}
        />
        <LocationTableActionPanel table={locationTable} clearLocationTable={clearTable} />
      </div>
      {(models || currentParam) && (
        <Section
          tag={t`column`}
          defaultOpen
          editableTitle={{
            defaultLabel: parameterUnit,
            label: customParameterTitle,
            onSave: (label) => updateTableNormalized({ title: label ?? parameterUnit }),
          }}
        >
          {models && (
            <FormField label={t`Source`}>
              <ModelDropdown
                options={models?.map((model: any) => ({ id: model.identifier, title: `${model.name}` })) || []}
                value={locationTable.model}
                onChange={(newModel) => {
                  updateTableNormalized({ model: newModel });
                }}
                placeholder={"Select a model"}
              />
            </FormField>
          )}
          {currentParam && (
            <>
              <WeatherParameterForm
                onSubmitParam={updateWeatherParameter}
                autofill={true}
                showExactParameter={true}
                parameter={currentParam}
                onChange={updateWeatherParameter}
              />
              {selectedModelSchema?.num_ensemble_members && (
                <EnsembleMemberSelect
                  ensSelect={locationTable.ensSelect ?? ""}
                  selectedModelSchema={selectedModelSchema}
                  onChange={({ ens_select }) => updateTableNormalized({ ensSelect: ens_select })}
                  allowMultipleEnsSelect={false}
                  custom={true}
                />
              )}
              {hasCalibratedAvailable(parameterUnit, locationTable.model) && (
                <CalibratedToggle
                  value={locationTable.calibrated}
                  change={(propsValue) => updateTableNormalized(propsValue)}
                />
              )}
              <FeatureHubGuard flag="api_url_suffix">
                <InitDateField
                  label={t`API URL suffix`}
                  defaultValue={locationTable.apiUrlSuffix ?? ""}
                  onChange={(apiUrlSuffix) => updateTableNormalized({ apiUrlSuffix })}
                />
              </FeatureHubGuard>
            </>
          )}
        </Section>
      )}

      <FormField label={t`Locations`}>
        <Button icon="my_location" onClick={openLocationManager}>
          <Trans>Manage</Trans>
        </Button>
      </FormField>
      <CompleteThreshold
        onMoveIndex={moveThresholdIdx}
        updateThresholds={handleThresholdsChange}
        updateAdvancedThresholds={handleAdvancedThresholdsChange}
        thresholds={locationTable.thresholds}
        advancedThresholds={locationTable.advancedThreshold}
      />
      <FormField label={t`Grouped rows`}>
        <input
          type="checkbox"
          checked={locationTable.areRowsGrouped}
          onChange={() => handleRowGroupingChange(!locationTable.areRowsGrouped)}
        />
      </FormField>
      <FormField label={t`Grouped columns`}>
        <input
          type="checkbox"
          checked={locationTable.areColumnsGrouped}
          onChange={() => handleColumnGroupingChange(!locationTable.areColumnsGrouped)}
        />
      </FormField>
      <FeatureHubGuard flag={"eon_table"}>
        {hasPolygon && (
          <>
            <FormField label={"Is Polygon"}>
              <MmCheckbox
                cid={"polygon-mode"}
                data-testid="polygon-mode"
                value={"polygon-mode"}
                checked={locationTable.isPolygon}
                toggleHandler={() => handleTableChange({ isPolygon: !locationTable.isPolygon })}
              />
            </FormField>
            <FormField label={"Show triggered"}>
              <MmCheckbox
                cid={"polygon-mode"}
                data-testid="polygon-mode"
                value={"polygon-mode"}
                checked={locationTable.showTriggered}
                toggleHandler={() => handleTableChange({ showTriggered: !locationTable.showTriggered })}
              />
            </FormField>
          </>
        )}
      </FeatureHubGuard>
      {exportAllowed && (
        <ButtonBar align="right" className="layer-stack-actions">
          <TableExportMenu onExportCsv={handleCSVExport} onExportPdf={handlePDFExport} />
        </ButtonBar>
      )}
    </>
  );
}
